<template>
    <app-dialog :isUserDetails="true" :title="$t('userDetails.title')" v-bind="$attrs" :value="value" @input="$emit('input', $event)" :width="getDialogWidth">
        <template v-slot:content>
            <v-form v-if="userStore.user" ref="form" lazy-validation>
                <v-text-field :readonly="!canEditNameOrEmail" :value="getUserName" @change="(v) => (formData.name = v)" :label="$t('userDetails.name')" :rules="[rules.required]" :autofocus="canEditNameOrEmail" required @keyup.enter="submit" data-cy="name" autocomplete="name"></v-text-field>

                <v-text-field :readonly="!canEditNameOrEmail" :value="getUserEmail" @change="(v) => (formData.email = v)" label="Email" :rules="[rules.required, rules.email]" type="email" required @keyup.enter="submit" data-cy="email" autocomplete="email"></v-text-field>
                <v-select v-if="canSwitchLanguage" :value="userStore.user.locale" :items="localeOptions" :label="$t('userDetails.language')" :loading="isSwitchingLocale" @change="switchLocale" data-cy="locale"></v-select>

                <v-checkbox v-if="isIOS()"
                 :value="getAutoSearchDisabled"
                 v-model="formData.auto_search_disabled"
                 :label="$t('userDetails.disableAutoSearch')"
                 @change="updateFormAutoSearch"
                ></v-checkbox>

                <div class="d-flex">
                    <v-text-field
                        v-model="formData.password"
                         :rules="formData.password ? [rules.required, rules.passwordStrength]:[]"
                        :type="showPassword ? 'text' : 'password'"
                        :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                        @click:append="showPassword = !showPassword"
                        @keyup.enter="submitForm"
                        @input="validatePasswordStrength"
                        label="Password"
                        :autofocus="!canEditNameOrEmail"
                        autocomplete="current-password"
                        required
                        class="mr-2 left-input"
                        data-cy="password"
                    ></v-text-field>

                    <v-text-field
                        v-model="formData.password_confirmation"
                        :rules="formData.password ? [rules.validLength, rules.passwordConfirmation(formData.password)] : []"
                        :type="showPassword ? 'text' : 'password'"
                        :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                        @click:append="showPassword = !showPassword"
                        @keyup.enter="submitForm"
                        :label="$t('userDetails.passwordConfirmation')"
                        autocomplete="current-password"
                        required
                        class="ml-2 left-input"
                        data-cy="password_confirmation"
                    ></v-text-field>
                </div>
                <div class="d-flex">
                    <!-- Password Strength Indicator -->
                    <div v-if="formData.password || formData.password_confirmation" class="password-requirements mb-4">
                        <div class="requirement-item" :class="{ valid: passwordValidation.minLength }">
                            <v-icon small :color="passwordValidation.minLength ? 'success' : 'grey'">
                                {{ passwordValidation.minLength ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="ml-2">{{ $t('userDetails.req.minLength') }}</span>
                        </div>
                        <div class="requirement-item" :class="{ valid: passwordValidation.hasUpperCase && passwordValidation.hasLowerCase }">
                            <v-icon small :color="passwordValidation.hasUpperCase && passwordValidation.hasLowerCase ? 'success' : 'grey'">
                                {{ passwordValidation.hasUpperCase ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="ml-2">{{ $t('userDetails.req.letterCase') }}</span>
                        </div>
                        <div class="requirement-item" :class="{ valid: passwordValidation.hasNumber && passwordValidation.hasSpecialChar }">
                            <v-icon small :color="passwordValidation.hasNumber && passwordValidation.hasSpecialChar? 'success' : 'grey'">
                                {{ passwordValidation.hasNumber && passwordValidation.hasSpecialChar ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="ml-2">{{ $t('userDetails.req.numberSpecial') }}</span>
                        </div>
                        <div class="requirement-item" :class="{ valid: formData.password_confirmation===formData.password  }">
                            <v-icon small :color="formData.password_confirmation===formData.password  && formData.password!=null && formData.password_confirmation!=null? 'success' : 'grey'">
                                {{ formData.password_confirmation===formData.password ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="ml-2">{{ $t('userDetails.req.match') }}</span>
                        </div>
                    </div>
                </div>
            </v-form>
        </template>

        <template v-slot:buttons="{}">
            <v-btn text class="myprovet-btn-cancel" @click="$emit('input', false)">{{ $t('common.close') }}</v-btn>
            <v-btn text class="myprovet-btn-ok" :loading="isLoading" @click="submit">{{ $t('common.change') }}</v-btn>
        </template>
    </app-dialog>
</template>

<script>
import { axios } from '@/Services/Backend';
import { mapStores } from 'pinia';
import { useUserStore } from '@/Stores/user';

export default {
    props: {
        value: {
            type: Boolean,
            required: true,
        },
    },

    data: () => ({
        passwordValidation: {
                minLength: false,
                hasUpperCase: false,
                hasLowerCase: false,
                hasNumber: false,
                hasSpecialChar: false,
        },
        formData: {
            name: null,
            email: null,
            password: null,
            password_confirmation: null,
            auto_search_disabled:false
        },
        showPassword: false,
        isLoading: false,
        isSwitchingLocale: false,
    }),

    computed: {
        ...mapStores(useUserStore),

        getUserName() {
            return this.userStore.user?.name;
        },

        getUserEmail() {
            return this.userStore.user?.email;
        },
        getAutoSearchDisabled() {
            this.formData.auto_search_disabled= this.userStore.user?.auto_search_disabled?true:false
            return this.userStore.user?.auto_search_disabled?true:false;
        },

        canSwitchLanguage() {
            return this.userStore.hasPermissionTo('Switch Language');
        },

        localeOptions() {
            return [
                { text: 'Português', value: 'pt' },
                { text: 'English', value: 'en' },
            ];
        },

        canEditNameOrEmail() {
            return this.userStore.hasPermissionTo('Update name and email (user details)');
        },

        getDialogWidth() {
            return this.getResponsiveDialogWidth('60vw', '35vw');
        },
        rules() {
            return {
                required: (value) => (value && !!value) || this.$t('validation.required'),
                passwordConfirmation: (password) => (value) => value === password || this.$t('userDetails.rules.passwordMismatch'),
                passwordStrength: (value) => this.isPasswordStrong(value) || this.$t('userDetails.rules.passwordWeak'),
                validLength: (value) => !value || value.length >= 8 || this.$t('userDetails.rules.minLength'),
                email: (value) => {
                    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return value && re.test(value) ? true : this.$t('userDetails.rules.invalidEmail');
                },
            };
        }
    },

    methods: {
        validatePasswordStrength() {
            const password = this.formData.password || '';

            this.passwordValidation = {
                minLength: password.length >= 8,
                hasUpperCase: /[A-Z]/.test(password),
                hasLowerCase: /[a-z]/.test(password),
                hasNumber: /[0-9]/.test(password),
                hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
            };
        },

        isPasswordStrong(password) {
            if (!password) return false;

            const minLength = password.length >= 8;
            const hasUpperCase = /[A-Z]/.test(password);
            const hasLowerCase = /[a-z]/.test(password);
            const hasNumber = /[0-9]/.test(password);
            const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

            return minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
        },

        switchLocale(locale) {
            this.isSwitchingLocale = true;

            // Saved right away: the locale is applied by refreshUser() through the user store
            axios
                .put('/api/user/update', { locale })
                .then(() => this.userStore.refreshUser())
                .catch(() => this.showError(this.$t('userDetails.saveError')))
                .finally(() => (this.isSwitchingLocale = false));
        },

        updateFormAutoSearch(val){
            this.formData.auto_search_disabled=val?true:false
        },
        submit() {
            if (!this.$refs.form.validate()) return;

            this.isLoading = true;

            axios
                .put('/api/user/update', this.formData)
                .then(() => this.userStore.refreshUser())
                .then(() => {
                    this.showSuccess(this.$t('userDetails.saved'));
                    this.$emit('input', false);
                })
                .catch(() => {
                    this.showError(this.$t('userDetails.saveError'));
                })
                .finally(() => (this.isLoading = false));
        },
    },
};
</script>

<style scoped>
.password-requirements {
    background-color: #f5f5f5;
    border-radius: 4px;
    padding: 12px;
}

.requirement-item {
    display: flex;
    align-items: center;
    font-size: 11px;
    color: #666;
    margin-bottom: 6px;
    transition: color 0.3s ease;
}

.requirement-item:last-child {
    margin-bottom: 0;
}

.requirement-item.valid {
    color: #4caf50;
}
</style>
